import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { tasksService, type TaskApi, type TaskChecklistItem, type TaskChecklistResponse } from '../../../services/tasksService';
import FilterMultiSelect from '../../common/FilterMultiSelect/FilterMultiSelect';
import DatePicker from '../../DatePicker/DatePicker';
import './TaskSubtasks.css';

const message = (error: any) => error?.response?.data?.message || error?.message || 'Не удалось сохранить пункт';
const dateLabel = (iso: string) => new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Moscow' });
const CalendarIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><rect x="4" y="5" width="16" height="16" rx="3" /><path d="M8 3v4m8-4v4M4 10h16m-11 4h6m-6 3h6" /></svg>;
const UserIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><circle cx="12" cy="12" r="9" /><circle cx="12" cy="9" r="3" /><path d="M6 19v-1a6 6 0 0 1 12 0v1" /></svg>;
type Draft = TaskChecklistItem & { performerIds: Set<number> };

const TaskSubtasks: React.FC<{ task: TaskApi }> = ({ task }) => {
  const queryClient = useQueryClient();
  const key = ['task', task.id, 'subtasks'];
  const query = useQuery({ queryKey: key, queryFn: () => tasksService.getSubtasks(task.id), refetchInterval: 5000 });
  const [title, setTitle] = useState('');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [dateOpen, setDateOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<number | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const refocusEntry = useRef(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const dateFieldRef = useRef<HTMLButtonElement>(null);
  const datePickerRef = useRef<HTMLDivElement>(null);
  const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
  const canManage = query.data?.can_manage ?? false;
  const rows = query.data?.data ?? [];
  const detailsOpen = draft !== null;
  useEffect(() => {
    if (!busy && refocusEntry.current) { refocusEntry.current = false; inputRef.current?.focus(); }
  }, [busy]);
  const closeDetails = () => { setDraft(null); setDateOpen(false); setError(null); };
  useEffect(() => {
    if (!detailsOpen) return;
    const previous = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    return () => { previous?.focus(); };
  }, [detailsOpen]);
  useEffect(() => {
    if (!detailsOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault(); event.stopImmediatePropagation();
        if (dateOpen) { setDateOpen(false); dateFieldRef.current?.focus(); }
        else if (!busy) { setDraft(null); setError(null); }
      }
      if (event.key === 'Tab') {
        const selector = 'button:not(:disabled), input:not(:disabled), textarea:not(:disabled), select:not(:disabled), [tabindex="0"]';
        const elements = [
          ...Array.from(dialogRef.current?.querySelectorAll<HTMLElement>(selector) ?? []),
          ...Array.from(datePickerRef.current?.querySelectorAll<HTMLElement>(selector) ?? []),
        ]
          .filter((element) => element.getClientRects().length > 0);
        const first = elements[0]; const last = elements[elements.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => { document.removeEventListener('keydown', onKey, true); };
  }, [detailsOpen, dateOpen, busy]);
  useLayoutEffect(() => {
    if (!dateOpen) return;
    const reposition = () => {
      const field = dateFieldRef.current?.getBoundingClientRect();
      const picker = datePickerRef.current?.getBoundingClientRect();
      if (!field || !picker) return;
      const below = field.bottom + 8;
      const above = field.top - picker.height - 8;
      setPickerPosition({
        left: Math.max(12, Math.min(field.left, window.innerWidth - picker.width - 12)),
        top: below + picker.height <= window.innerHeight - 12 ? below
          : above >= 12 ? above : Math.max(12, window.innerHeight - picker.height - 12),
      });
    };
    reposition();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(reposition) : null;
    if (datePickerRef.current) observer?.observe(datePickerRef.current);
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => { observer?.disconnect(); window.removeEventListener('resize', reposition); window.removeEventListener('scroll', reposition, true); };
  }, [dateOpen]);
  useEffect(() => {
    if (!dateOpen) return;
    const onOutside = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!datePickerRef.current?.contains(target) && !dateFieldRef.current?.contains(target)) setDateOpen(false);
    };
    document.addEventListener('pointerdown', onOutside, true);
    return () => document.removeEventListener('pointerdown', onOutside, true);
  }, [dateOpen]);
  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: key }),
      queryClient.invalidateQueries({ queryKey: ['task', task.id, 'activity'] }),
    ]);
  };
  const remember = (item: TaskChecklistItem) => {
    queryClient.setQueryData<TaskChecklistResponse>(key, (current) => {
      if (!current) return current;
      const data = current.data.some((row) => row.id === item.id) ? current.data.map((row) => row.id === item.id ? item : row) : [...current.data, item];
      return { ...current, data, total: data.length, completed: data.filter((row) => row.completed).length };
    });
  };
  const createItem = async () => {
    if (!title.trim() || busy || !canManage) return;
    setBusy(true); setError(null);
    try { remember(await tasksService.createSubtask(task.id, { title: title.trim() })); setTitle(''); refocusEntry.current = true; await refresh(); }
    catch (failure) { setError(message(failure)); }
    finally { setBusy(false); }
  };
  const toggle = async (item: TaskChecklistItem) => {
    if (busy || !canManage) return;
    setBusy(true); setError(null);
    try {
      const updated = await tasksService.updateSubtask(task.id, item.id, { completed: !item.completed });
      remember(updated);
      setDraft((current) => current?.id === item.id ? { ...current, completed: updated.completed } : current);
      await refresh();
    } catch (failure) { setError(message(failure)); }
    finally { setBusy(false); }
  };
  const openDetails = (item: TaskChecklistItem) => {
    setDraft({ ...item, performerIds: new Set(item.performers.map((user) => user.id)) });
    setDateOpen(false); setError(null);
  };
  const saveDetails = async (event: React.FormEvent) => {
    event.preventDefault(); if (!draft || busy || !canManage || !draft.title.trim()) return;
    setBusy(true); setError(null);
    try {
      const updated = await tasksService.updateSubtask(task.id, draft.id, {
        title: draft.title.trim(), description: draft.description?.trim() || null, deadline: draft.deadline,
        priority: draft.priority === 'high' ? 'high' : 'normal', performer_ids: Array.from(draft.performerIds),
      });
      remember(updated); closeDetails(); await refresh();
    } catch (failure) { setError(message(failure)); }
    finally { setBusy(false); }
  };
  const remove = async (itemId: number) => {
    if (busy || !canManage) return;
    setBusy(true); setError(null);
    try { await tasksService.deleteSubtask(task.id, itemId); setDeleteTarget(null); await refresh(); }
    catch (failure) { setError(message(failure)); }
    finally { setBusy(false); }
  };
  const memberOptions = [...(task.assigner ? [task.assigner] : []), ...task.performers]
    .filter((user, index, members) => members.findIndex((other) => other.id === user.id) === index)
    .map((user) => ({ id: user.id, name: user.name }));

  return <section className="task-subtasks" aria-label="Подзадачи">
    <div className="task-subtasks-heading"><h3>Подзадачи</h3><span>{query.data?.completed ?? 0}/{query.data?.total ?? 0}</span></div>
    {error && !draft && <p className="task-subtasks-error" role="alert">{error}</p>}
    {query.isPending && <p className="task-subtasks-empty">Загрузка…</p>}
    {query.isError && <p className="task-subtasks-error" role="alert">Не удалось загрузить чек-лист. <button type="button" onClick={() => void query.refetch()}>Повторить</button></p>}
    <ul className="task-checklist">{rows.map((item) => <li key={item.id}>
      <div className="task-checklist-row">
        <input type="checkbox" className="task-checklist-check" aria-label={`Выполнено: ${item.title}`} checked={item.completed} disabled={!canManage || busy} onChange={() => void toggle(item)} />
        <button type="button" className={`task-checklist-title${item.completed ? ' is-completed' : ''}`} onClick={() => openDetails(item)}>{item.title}</button>
        {item.deadline && <span className="task-checklist-deadline">{item.deadline_formatted || dateLabel(item.deadline)}</span>}
        <button type="button" className="task-checklist-icon" aria-label={`Детали: ${item.title}`} title={item.performers.map((user) => user.name).join(', ') || 'Описание и участники'} onClick={() => openDetails(item)}><UserIcon /></button>
        {canManage && <button type="button" className="task-checklist-remove" aria-label={`Удалить: ${item.title}`} title="Удалить пункт" disabled={busy} onClick={() => setDeleteTarget(item.id)}>×</button>}
      </div>
      {deleteTarget === item.id && <div className="task-checklist-confirm"><span>Удалить этот пункт?</span><button type="button" disabled={busy} onClick={() => void remove(item.id)}>Удалить</button><button type="button" onClick={() => setDeleteTarget(null)}>Отмена</button></div>}
    </li>)}</ul>
    {canManage && <form className="task-checklist-entry" onSubmit={(event) => { event.preventDefault(); void createItem(); }}>
      <button type="submit" aria-label="Добавить пункт" disabled={busy || !title.trim()}>⊕</button>
      <div><textarea ref={inputRef} rows={1} maxLength={500} aria-label="Новая подзадача" placeholder="Новая подзадача" value={title} disabled={busy} onChange={(event) => setTitle(event.target.value)}
        onKeyDown={(event) => { if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) { event.preventDefault(); void createItem(); } }} /><span><kbd>Enter</kbd> для создания</span></div>
    </form>}
    {draft && createPortal(<div className="task-checklist-overlay" onClick={(event) => { event.stopPropagation(); if (event.target === event.currentTarget && !busy) closeDetails(); }}>
      <div className="task-checklist-dialog" ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="checklist-dialog-title" tabIndex={-1}>
        <header><h3 id="checklist-dialog-title">Пункт чек-листа</h3><button type="button" aria-label="Закрыть детали подзадачи" disabled={busy} onClick={closeDetails}>×</button></header>
        <form onSubmit={(event) => void saveDetails(event)}>
          <label className="task-checklist-completed"><input type="checkbox" checked={draft.completed} disabled={busy || !canManage} onChange={() => void toggle(draft)} />Выполнено</label>
          <label>Название<input value={draft.title} maxLength={500} readOnly={!canManage} disabled={busy} onChange={(event) => setDraft({ ...draft, title: event.target.value })} required /></label>
          <label>Описание<textarea rows={4} value={draft.description ?? ''} maxLength={10000} readOnly={!canManage} disabled={busy} placeholder="Добавьте описание" onChange={(event) => setDraft({ ...draft, description: event.target.value })} /></label>
          <div><span className="task-checklist-label">Срок</span><div className="task-checklist-date"><button ref={dateFieldRef} type="button" disabled={busy || !canManage} aria-haspopup="dialog" aria-expanded={dateOpen} aria-controls={dateOpen ? 'task-checklist-datepicker' : undefined} onClick={() => setDateOpen((current) => !current)}>{draft.deadline ? dateLabel(draft.deadline) : 'Указать срок'}<CalendarIcon /></button>{draft.deadline && canManage && <button type="button" aria-label="Убрать срок" disabled={busy} onClick={() => setDraft({ ...draft, deadline: null })}>×</button>}</div>
          </div>
          {canManage ? <div><span className="task-checklist-label">Участники</span><FilterMultiSelect variant="report" showLabel={false} options={memberOptions} selectedIds={draft.performerIds} placeholder="Не выбраны" onToggle={(id) => setDraft((current) => { if (!current) return current; const next = new Set(current.performerIds); if (next.has(Number(id))) next.delete(Number(id)); else next.add(Number(id)); return { ...current, performerIds: next }; })} /></div>
            : <p className="task-checklist-readonly-members">{draft.performers.map((user) => user.name).join(', ') || 'Участники не выбраны'}</p>}
          <label>Приоритет<select value={draft.priority} disabled={!canManage || busy} onChange={(event) => setDraft({ ...draft, priority: event.target.value })}><option value="normal">Обычный</option><option value="high">Высокий</option></select></label>
          {error && <p className="task-subtasks-error" role="alert">{error}</p>}
          <footer><button type="button" disabled={busy} onClick={closeDetails}>{canManage ? 'Отмена' : 'Закрыть'}</button>{canManage && <button type="submit" disabled={busy || !draft.title.trim()}>{busy ? 'Сохранение…' : 'Сохранить'}</button>}</footer>
        </form>
      </div>
    </div>, document.body)}
    {draft && dateOpen && createPortal(<div id="task-checklist-datepicker" ref={datePickerRef} className="task-checklist-datepicker" role="dialog" aria-label="Выбор срока подзадачи" style={pickerPosition} onClick={(event) => event.stopPropagation()}>
      <DatePicker value={draft.deadline ? new Date(draft.deadline) : new Date()} onConfirm={(date) => {
        setDraft((current) => current ? { ...current, deadline: date.toISOString() } : current);
        setDateOpen(false); dateFieldRef.current?.focus();
      }} onClose={() => { setDateOpen(false); dateFieldRef.current?.focus(); }} />
    </div>, document.body)}
  </section>;
};

export default TaskSubtasks;
